import { ref } from 'vue';
import MessageApi from 'dashboard/api/inbox/message';
import ContactAPI from 'dashboard/api/contacts';

const MESSAGE_PAGE_SIZE = 20;
const CONVERSATION_PAGE_SIZE = 20;
const EMPTY_CONVERSATION_SKIP_LIMIT = 20;

/**
 * Scroll-up order (LiveChat-style): older pages of the open chat first. Once it is fully loaded
 * (its last older page came back empty), the same gesture continues into earlier conversations.
 * `withHistory: false` is for programmatic scrolls (opening a chat, jumping to a message).
 */
export const loadOlderOnScrollUp = async ({
  isChatFullyLoaded,
  loadOlderCurrent,
  loadHistory,
  withHistory = true,
}) => {
  if (!isChatFullyLoaded()) await loadOlderCurrent();
  if (withHistory && isChatFullyLoaded()) await loadHistory();
};

// No scroll event fires once the panel is at the top, or at all in a chat too short to scroll,
// so wheeling up there is what keeps loading older history.
export const isWheelUpAtTop = (event, panel) =>
  event.deltaY < 0 && Boolean(panel) && panel.scrollTop <= 0;

/**
 * The contact's earlier conversations, loaded on demand above the open chat, newest first.
 * Each call to loadOlder() adds one step: an older page of the conversation being read, or the
 * next earlier conversation (empty ones are skipped). reset() drops everything and cancels
 * in-flight requests, so a slow response for the previous chat can't land in the new one.
 *
 * @param {() => object} getChat Returns the open conversation ({ id, meta: { sender: { id } } }).
 */
export function useContactHistory(getChat) {
  const blocks = ref([]);
  const exhausted = ref(false);
  const isLoading = ref(false);

  let queue = [];
  let beforeId = null;
  let listExhausted = false;
  let activeId = null;
  let requestId = 0;

  const reset = () => {
    requestId += 1;
    blocks.value = [];
    exhausted.value = false;
    isLoading.value = false;
    queue = [];
    beforeId = null;
    listExhausted = false;
    activeId = null;
  };

  const fetchConversationPage = async id => {
    const chat = getChat();
    const contactId = chat?.meta?.sender?.id;
    if (!contactId || !chat?.id) {
      listExhausted = true;
      return;
    }

    const { data } = await ContactAPI.getConversations(
      contactId,
      beforeId || chat.id
    );
    if (id !== requestId) return;

    const payload = data?.payload || [];
    if (payload.length < CONVERSATION_PAGE_SIZE) listExhausted = true;
    if (!payload.length) return;

    beforeId = payload[payload.length - 1].id;
    const seen = new Set([
      Number(chat.id),
      ...blocks.value.map(block => Number(block.id)),
      ...queue.map(conversation => Number(conversation.id)),
    ]);
    payload.forEach(conversation => {
      if (seen.has(Number(conversation.id))) return;
      queue.push(conversation);
      seen.add(Number(conversation.id));
    });
  };

  const nextConversation = async id => {
    if (!queue.length && !listExhausted) await fetchConversationPage(id);
    return queue.shift() || null;
  };

  // Older page of the conversation being read. Returns true when it added messages.
  const prependOlder = async (conversationId, id) => {
    const block = blocks.value.find(item => item.id === conversationId);
    const oldestId = block?.messages[0]?.id;
    if (!oldestId) return false;

    const { data } = await MessageApi.getPreviousMessages({
      conversationId,
      before: oldestId,
    });
    if (id !== requestId) return false;

    const payload = data?.payload || [];
    if (payload.length < MESSAGE_PAGE_SIZE) activeId = null;
    const existingIds = new Set(block.messages.map(message => message.id));
    const fresh = payload.filter(message => !existingIds.has(message.id));
    if (fresh.length) block.messages = fresh.concat(block.messages);
    return fresh.length > 0;
  };

  // Next earlier conversation with messages. Returns true when a block was added.
  const prependNextConversation = async id => {
    for (
      let skipped = 0;
      skipped <= EMPTY_CONVERSATION_SKIP_LIMIT;
      skipped += 1
    ) {
      // eslint-disable-next-line no-await-in-loop
      const conversation = await nextConversation(id);
      if (id !== requestId) return false;
      if (!conversation) {
        exhausted.value = true;
        return false;
      }

      // eslint-disable-next-line no-await-in-loop
      const { data } = await MessageApi.getPreviousMessages({
        conversationId: conversation.id,
      });
      if (id !== requestId) return false;

      const messages = data?.payload || [];
      if (messages.length) {
        blocks.value.unshift({
          id: conversation.id,
          createdAt: conversation.created_at,
          messages,
        });
        if (messages.length >= MESSAGE_PAGE_SIZE) activeId = conversation.id;
        return true;
      }
    }
    return false;
  };

  const loadOlder = async () => {
    if (isLoading.value || exhausted.value) return false;

    const id = requestId;
    isLoading.value = true;
    try {
      if (activeId) {
        if (await prependOlder(activeId, id)) return true;
        if (id !== requestId) return false;
        activeId = null;
      }
      return await prependNextConversation(id);
    } finally {
      if (id === requestId) isLoading.value = false;
    }
  };

  return { blocks, exhausted, isLoading, reset, loadOlder };
}
